import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  hasPlaceholderPermission,
  useCanViewEmployeeDocuments,
  useCanViewEmployeeNotes,
  usePermission,
} from '@/lib/permissions';
import { useState } from 'react';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useEscapeKey } from '@/hooks/use-escape-key';
import { ChevronLeft } from 'lucide-react';
import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { useEmployee } from '@/hooks/use-employee';
import { reactivateEmployee, deleteEmployee } from '@/lib/employees';
import { extractErrorMessage } from '@/lib/errors';
import { ReassignReportsDialog } from '@/features/people/components/reassign-reports-dialog';
import {
  deactivateWithReportHandoff,
  type ReportReassignment,
  type UnsettledDirectReport,
} from '@/features/people/lib/deactivation-reports';
import { employeesKeys } from '@/hooks/use-employees';
import { useQueryClient } from '@tanstack/react-query';
import { PreboardingConflictBanner } from '@/features/preboarding-forms/review/preboarding-conflict-banner';
import { ProfileCard } from '@/features/people/components/profile/profile-card';
import {
  isProfileTabId,
  resolveProfileTab,
  type ProfileTabAccess,
  type ProfileTabId,
} from '@/features/people/lib/profile-tabs';
import { JobTab } from '@/features/people/components/profile/job-tab';
import { EmergencyTab } from '@/features/people/components/profile/emergency-tab';
import { DocumentsTab } from '@/features/people/components/profile/documents-tab';
import { AssetsTab } from '@/features/people/components/profile/assets-tab';
import { AccessTab } from '@/features/people/components/profile/access-tab';
import { NotesTab } from '@/features/people/components/profile/notes-tab';
import { AbsencesTab } from '@/features/people/components/profile/absences-tab';
import { CompensationsTab } from '@/features/people/components/profile/compensations-tab';
import { HistoryTab } from '@/features/people/components/profile/history-tab';
import { ProfileCardSkeleton } from '@/features/people/components/profile/profile-card-skeleton';
import { EditEmployeeDialog } from '@/features/people/components/profile/edit-employee-dialog';
import { EditAvatarDialog } from '@/features/people/components/profile/edit-avatar-dialog';
import { useEmployeeAssets } from '@/hooks/use-assets';
import { useEmployeeAccessGrants } from '@/hooks/use-access-grants';

export const Route = createFileRoute('/employees/$id')({
  beforeLoad: async () => {
    const { isAuthenticated, isInitialized } = useAuthStore.getState();
    if (!isInitialized) return;
    if (!isAuthenticated) throw redirect({ to: '/login' });

    const user = useAuthStore.getState().user;
    if (hasPlaceholderPermission(user)) throw redirect({ to: '/placeholder' });
  },
  component: EmployeeProfilePage,
});

function getInitialTab(): ProfileTabId {
  if (typeof window === 'undefined') return 'job';
  const tab = new URLSearchParams(window.location.search).get('tab');
  return isProfileTabId(tab) ? tab : 'job';
}

function EmployeeProfilePage() {
  const formatName = useEmployeeNameFormatter();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ProfileTabId>(getInitialTab);
  const canEditEmployee = usePermission('employees.profile.edit');
  const canDeleteEmployee = usePermission('employees.profile.delete');
  const canImpersonate = usePermission('auth.impersonate');
  const isImpersonating = useAuthStore((s) => s.isImpersonating);
  const startImpersonation = useAuthStore((s) => s.startImpersonation);
  // Set only by the Job tab's "Show all history" button, so the History tab moves
  // keyboard focus to its heading when opened that way and not on a plain tab click.
  const [focusHistory, setFocusHistory] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editAvatarDialogOpen, setEditAvatarDialogOpen] = useState(false);
  const [deactivateDialog, setDeactivateDialog] = useState<{
    open: boolean;
    isReactivate: boolean;
  }>({ open: false, isReactivate: false });
  const [lastWorkingDay, setLastWorkingDay] = useState(
    () => new Date().toISOString().split('T')[0],
  );
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
  }>({ open: false });
  // Set when the backend refuses the deactivation because this employee still
  // has direct reports — carries the list into the reassignment dialog.
  const [reportHandoff, setReportHandoff] = useState<UnsettledDirectReport[] | null>(null);
  const [isHandingOff, setIsHandingOff] = useState(false);

  const { data: employee, isLoading, error } = useEmployee(id);
  const canViewDocuments = useCanViewEmployeeDocuments(employee?.id);
  const canViewAssets = usePermission('assets.view');
  const canViewAccess = usePermission('access.view');
  const canViewNotes = useCanViewEmployeeNotes(employee?.id);
  const canViewHistory = usePermission('employees.audit.view');
  // One answer for ProfileCard's tab bar and the `?tab=` fallback below, which
  // both ask `canOpenProfileTab` — so the two agree on Assets, Access, Notes and
  // History. [HW-940] [HW-795] [HW-1053]
  const tabAccess: ProfileTabAccess = {
    canViewDocuments,
    canViewAssets,
    canViewAccess,
    canViewNotes,
    canViewHistory,
  };
  // The lists behind the badges are not requested without the permission —
  // the request could only 403. [HW-940]
  const { data: employeeAssets } = useEmployeeAssets(employee?.id || '', {
    enabled: tabAccess.canViewAssets,
  });
  const { data: employeeAccess } = useEmployeeAccessGrants(employee?.id || '', {
    enabled: tabAccess.canViewAccess,
  });

  // `?tab=` mounts a tab straight from the URL, bypassing the section list in
  // ProfileCard — so a bookmarked or hand-typed link would still render the 403
  // for someone who may not read this profile's documents [HW-785], assets,
  // access [HW-940] or notes [HW-1053].
  const visibleTab: ProfileTabId = resolveProfileTab(activeTab, tabAccess);

  // Escape navigates back to the People list — but stands down when a nearer handler
  // already consumed it (Radix dialogs / inline edits) or focus is in a text field
  // (e.g. the Notes "Search notes…" input).
  useEscapeKey(() => navigate({ to: '/people' }), { ignoreEditable: true });

  const handleTabChange = (tab: ProfileTabId) => {
    setFocusHistory(false);
    setActiveTab(tab);
    // Update URL without navigation
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.replaceState({}, '', url);
  };

  const handleBack = () => {
    navigate({ to: '/people' });
  };

  const handleEdit = () => {
    setEditDialogOpen(true);
  };

  const handleDeactivate = () => {
    if (!employee) return;
    const isTerminated = employee.status === 'terminated';
    setLastWorkingDay(new Date().toISOString().split('T')[0]);
    setDeactivateDialog({
      open: true,
      isReactivate: isTerminated,
    });
  };

  const handleDelete = () => {
    if (!employee) return;
    setDeleteDialog({ open: true });
  };

  const handleImpersonate = async () => {
    if (!employee) return;
    try {
      await startImpersonation(employee.id);
      navigate({ to: '/' });
    } catch {
      toast.error('Failed to impersonate employee. Please try again.');
    }
  };

  const afterStatusChange = async (employeeId: string) => {
    // Invalidate employee queries to refresh the data
    await queryClient.invalidateQueries({ queryKey: employeesKeys.all });
    await queryClient.invalidateQueries({ queryKey: employeesKeys.stats() });
    await queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });

    setDeactivateDialog({ open: false, isReactivate: false });
  };

  const confirmDeactivate = async () => {
    if (!employee) return;

    try {
      if (deactivateDialog.isReactivate) {
        await reactivateEmployee(employee.id);
      } else {
        const result = await deactivateWithReportHandoff(employee.id, lastWorkingDay);
        if (!result.ok) {
          // Nothing changed yet — the reassignment dialog takes it from here.
          setDeactivateDialog({ open: false, isReactivate: false });
          setReportHandoff(result.reports);
          return;
        }
      }
      await afterStatusChange(employee.id);
    } catch (error) {
      // The refusals carry their reason — MANAGER_NO_LONGER_ACTIVE, a malformed
      // handover, a missing permission — and swallowing them would leave the
      // operator retrying the same rejected request.
      toast.error(
        extractErrorMessage(
          error,
          `Failed to ${deactivateDialog.isReactivate ? 'reactivate' : 'deactivate'} employee. Please try again.`
        )
      );
    }
  };

  const confirmReportHandoff = async (assignments: ReportReassignment[]) => {
    if (!employee) return;
    setIsHandingOff(true);
    try {
      const result = await deactivateWithReportHandoff(employee.id, lastWorkingDay, assignments);
      if (!result.ok) {
        // Someone gained a report while the dialog was open — ask again.
        setReportHandoff(result.reports);
        toast.error('The list of direct reports changed. Please review it again.');
        return;
      }
      setReportHandoff(null);
      await afterStatusChange(employee.id);
    } catch (error) {
      toast.error(
        extractErrorMessage(error, 'Failed to deactivate employee. Please try again.')
      );
    } finally {
      setIsHandingOff(false);
    }
  };

  const confirmDelete = async () => {
    if (!employee) return;

    try {
      await deleteEmployee(employee.id);
      // Invalidate employee queries
      await queryClient.invalidateQueries({ queryKey: employeesKeys.all });
      await queryClient.invalidateQueries({ queryKey: employeesKeys.stats() });
      // Navigate back to people page after successful deletion
      navigate({ to: '/people' });
    } catch {
      toast.error('Failed to delete employee. Please try again.');
    }
  };

  // Error state
  if (error) {
    return (
      <AuthGuard>
        <AppLayout>
          <div className="flex min-h-[60vh] flex-col items-center justify-center p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-foreground">
                {error.message.includes('404')
                  ? 'Employee Not Found'
                  : 'Error Loading Profile'}
              </h2>
              <p className="mt-2 text-muted-foreground">
                {error.message.includes('404')
                  ? "The employee you're looking for doesn't exist or has been removed."
                  : 'Something went wrong while loading the employee profile.'}
              </p>
              <button
                onClick={handleBack}
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline cursor-pointer"
              >
                <ChevronLeft className="h-4 w-4" />
                Back to People
              </button>
            </div>
          </div>
        </AppLayout>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <AppLayout>
        {/* Page Header */}
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <button
              onClick={handleBack}
              className="hover:text-foreground transition-colors cursor-pointer"
            >
              People
            </button>
            <span>/</span>
            <span className="font-medium text-foreground">
              {isLoading
                ? 'Loading...'
                : employee ? formatName({ firstName: employee.firstName, lastName: employee.lastName }) : ''}
            </span>
          </div>
        </header>

        {/* Main content */}
        <div className="p-6 max-w-6xl">
          {/* Back navigation */}
          <button
            onClick={handleBack}
            className="mb-5 inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors -ml-2 cursor-pointer"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to People
          </button>

          {/* Profile Card */}
          {isLoading ? (
            <ProfileCardSkeleton />
          ) : employee ? (
            <>
              <PreboardingConflictBanner
                employeeId={employee.id}
                autoOpen={
                  new URLSearchParams(window.location.search).get('review') === 'preboarding'
                }
              />
              <ProfileCard
                employee={employee}
                activeTab={visibleTab}
                onTabChange={handleTabChange}
                onEdit={canEditEmployee ? handleEdit : undefined}
                onEditAvatar={canEditEmployee ? () => setEditAvatarDialogOpen(true) : undefined}
                onImpersonate={canImpersonate && !isImpersonating ? handleImpersonate : undefined}
                onDeactivate={canEditEmployee ? handleDeactivate : undefined}
                onDelete={canDeleteEmployee ? handleDelete : undefined}
                tabAccess={tabAccess}
                assetCount={employeeAssets?.filter((a) => a.status === 'ASSIGNED').length}
                accessCount={employeeAccess?.filter((a) => a.status === 'ACTIVE').length}
              />

              {/* Tab Content */}
              <div className="mt-6">
                {visibleTab === 'job' && (
                  <JobTab
                    employee={employee}
                    canViewHistory={tabAccess.canViewHistory}
                    onShowAllHistory={() => {
                      handleTabChange('history');
                      setFocusHistory(true);
                    }}
                  />
                )}
                {visibleTab === 'emergency' && <EmergencyTab employee={employee} />}
                {visibleTab === 'documents' && (
                  <DocumentsTab employee={employee} canViewDocuments={canViewDocuments} />
                )}
                {visibleTab === 'assets' && <AssetsTab employee={employee} />}
                {visibleTab === 'access' && <AccessTab employee={employee} />}
                {visibleTab === 'notes' && <NotesTab employee={employee} />}
                {visibleTab === 'absences' && <AbsencesTab employee={employee} />}
                {visibleTab === 'compensations' && <CompensationsTab employee={employee} />}
                {visibleTab === 'history' && (
                  <HistoryTab
                    employee={employee}
                    focusOnMount={focusHistory}
                    onFocused={() => setFocusHistory(false)}
                  />
                )}
              </div>

              {/* Edit Employee Dialog */}
              {employee && (
                <>
                  <EditEmployeeDialog
                    open={editDialogOpen}
                    onOpenChange={setEditDialogOpen}
                    employee={employee}
                  />
                  <EditAvatarDialog
                    open={editAvatarDialogOpen}
                    onOpenChange={setEditAvatarDialogOpen}
                    employee={employee}
                  />
                </>
              )}
            </>
          ) : null}
        </div>

        {/* Deactivate/Reactivate Confirmation Dialog */}
        <AlertDialog open={deactivateDialog.open} onOpenChange={(open) => {
          if (!open) {
            setDeactivateDialog({ open: false, isReactivate: false });
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {deactivateDialog.isReactivate ? 'Reactivate Employee' : 'Deactivate Employee'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deactivateDialog.isReactivate ? (
                  <>
                    Are you sure you want to reactivate <strong>{employee ? formatName({ firstName: employee.firstName, lastName: employee.lastName }) : 'this employee'}</strong>? Their status will be changed to active and termination date will be cleared.
                  </>
                ) : (
                  <>
                    Are you sure you want to deactivate <strong>{employee ? formatName({ firstName: employee.firstName, lastName: employee.lastName }) : 'this employee'}</strong>? Their status will be changed to terminated.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {!deactivateDialog.isReactivate && (
              <div className="space-y-2 py-2">
                <Label htmlFor="last-working-day">Last Working Day</Label>
                <Input
                  id="last-working-day"
                  type="date"
                  value={lastWorkingDay}
                  onChange={(e) => setLastWorkingDay(e.target.value)}
                  className="dark:[color-scheme:dark]"
                />
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeactivate}
                variant={deactivateDialog.isReactivate ? 'default' : 'destructive'}
                className={deactivateDialog.isReactivate ? 'bg-green-600 hover:bg-green-700 text-white' : undefined}
              >
                {deactivateDialog.isReactivate ? 'Reactivate' : 'Deactivate'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reassign direct reports before this manager can be deactivated */}
        {reportHandoff && employee && (
          <ReassignReportsDialog
            open
            onOpenChange={(open) => {
              if (!open) setReportHandoff(null);
            }}
            employeeId={employee.id}
            employeeName={formatName({
              firstName: employee.firstName,
              lastName: employee.lastName,
              preferredName: employee.preferredName,
            })}
            reports={reportHandoff}
            onConfirm={confirmReportHandoff}
            isSubmitting={isHandingOff}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => {
          if (!open) {
            setDeleteDialog({ open: false });
          }
        }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Employee</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to permanently delete <strong>{employee ? formatName({ firstName: employee.firstName, lastName: employee.lastName }) : 'this employee'}</strong>? This action cannot be undone and will remove all employee data from the database.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                variant="destructive"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </AppLayout>
    </AuthGuard>
  );
}

